import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  LinearProgress,
  Fade,
  Divider,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  AccessTime as DurationIcon,
  Download as DownloadIcon, // <-- Added Download Icon
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

// ─── Inline Audio Player ──────────────────────────────────────────────────────

const AudioPlayer = ({ url, durationReadable }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("0:00");

  const toggle = useCallback(
    (e) => {
      e.stopPropagation();
      if (!audioRef.current) return;
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setPlaying((p) => !p);
    },
    [playing]
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      const pct = audio.duration
        ? (audio.currentTime / audio.duration) * 100
        : 0;
      setProgress(pct);
      const m = Math.floor(audio.currentTime / 60);
      const s = Math.floor(audio.currentTime % 60)
        .toString()
        .padStart(2, "0");
      setCurrentTime(`${m}:${s}`);
    };

    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
      setCurrentTime("0:00");
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: playing ? "primary.50" : "grey.50",
        border: "1px solid",
        borderColor: playing ? "primary.200" : "grey.200",
        borderRadius: 2,
        px: 1.2,
        py: 0.6,
        minWidth: 160,
        maxWidth: 200,
        transition: "all 0.2s ease",
        "&:hover": { borderColor: "primary.300", bgcolor: "primary.50" },
      }}
    >
      <audio ref={audioRef} src={url} preload="metadata" />

      <Tooltip title={playing ? "Pause" : "Play preview"}>
        <IconButton
          size="small"
          onClick={toggle}
          sx={{
            p: 0.5,
            flexShrink: 0,
            bgcolor: playing ? "primary.main" : "primary.100",
            color: playing ? "white" : "primary.main",
            transition: "all 0.15s ease",
            "&:hover": { bgcolor: playing ? "primary.dark" : "primary.200" },
          }}
        >
          {playing ? (
            <PauseIcon sx={{ fontSize: 16 }} />
          ) : (
            <PlayIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Tooltip>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            borderRadius: 2,
            bgcolor: "grey.200",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              bgcolor: playing ? "primary.main" : "grey.400",
              transition: "none",
            },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.3 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1 }}
          >
            {currentTime}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: 10, color: "text.secondary", lineHeight: 1 }}
          >
            {durationReadable}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Row Actions Dropdown ─────────────────────────────────────────────────────

// Added onDownload prop
const RowActionsMenu = ({ file, onView, onDelete, onDownload }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const act = (fn) => () => {
    handleClose();
    fn(file);
  };

  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 150,
            borderRadius: 2,
            "& .MuiMenuItem-root": { fontSize: 14, gap: 1.2, py: 0.9 },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={act(onView)}>
          <VisibilityIcon fontSize="small" sx={{ color: "text.secondary" }} />
          View Details
        </MenuItem>

        {/* Added Download Menu Item */}
        <MenuItem onClick={act(onDownload)}>
          <DownloadIcon fontSize="small" sx={{ color: "text.secondary" }} />
          Download
        </MenuItem>

        <Divider />

        <MenuItem onClick={act(onDelete)}>
          <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          <Typography sx={{ color: "error.main" }}>Delete</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const VoiceFilesList = () => {
  const navigate = useNavigate();

  const [voiceFiles, setVoiceFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    file: null,
    loading: false,
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchVoiceFiles = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/multipart/voice-files`,
        {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            search: debouncedSearch,
            status: statusFilter,
            sortBy: "createdAt",
            sortOrder: "desc",
          },
          ...getHeader(),
        }
      );
      setVoiceFiles(response.data.data);
      setTotalCount(response.data.pagination.totalCount);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch voice files");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoiceFiles();
  }, [page, rowsPerPage, debouncedSearch, statusFilter]);

  const handleDeleteConfirm = async () => {
    setDeleteModal((p) => ({ ...p, loading: true }));
    try {
      await axios.delete(
        `${API_ENDPOINT}/multipart/voice-files/${deleteModal.file._id}`,
        getHeader()
      );
      setDeleteModal({ open: false, file: null, loading: false });
      fetchVoiceFiles();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete voice file");
      setDeleteModal((p) => ({ ...p, loading: false }));
    }
  };

  // <-- Added Robust Download Handler -->
  const handleDownloadFile = async (file) => {
    if (!file.voiceFileUrl) {
      setError("No file URL available to download.");
      return;
    }
    try {
      // Fetch the file to bypass browser's default media player behavior
      const response = await fetch(file.voiceFileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = file.fileName || "voice-file";
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      // Fallback if fetch fails (e.g. CORS block)
      const link = document.createElement("a");
      link.href = file.voiceFileUrl;
      link.download = file.fileName || "voice-file";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      case "completed":
        return "success";
      case "uploading":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (date) => {
    try {
      return format(new Date(date), "MMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Voice Files
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <a
              href="https://hoineki.com/audio-to-wav-converter"
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              underline="hover"
            >
              Convert to WAV
            </a>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => navigate("/home/upload-files")}
            >
              Upload New File
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search by filename, user ID, category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchVoiceFiles} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>File ID</strong>
                </TableCell>
                <TableCell>
                  <strong>File Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Category</strong>
                </TableCell>
                <TableCell>
                  <strong>Size / Duration</strong>
                </TableCell>
                <TableCell>
                  <strong>Preview</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Created On</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : voiceFiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      {debouncedSearch || statusFilter
                        ? "No voice files found matching your criteria"
                        : "No voice files uploaded yet"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                voiceFiles.map((file) => (
                  <TableRow key={file._id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontSize: 12 }}
                      >
                        {file?.voiceFileId ?? "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={file.originalFileName}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.fileName}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={file.promptCategory}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>

                    {/* Size + Duration */}
                    <TableCell>
                      <Stack spacing={0.3}>
                        <Typography variant="body2">
                          {file.fileSizeReadable}
                        </Typography>
                        {file.durationReadable && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                              color: "text.secondary",
                            }}
                          >
                            <DurationIcon sx={{ fontSize: 12 }} />
                            <Typography variant="caption">
                              {file.durationReadable}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </TableCell>

                    {/* Audio Preview */}
                    <TableCell>
                      {file.voiceFileUrl ? (
                        <AudioPlayer
                          url={file.voiceFileUrl}
                          durationReadable={file.durationReadable ?? ""}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={file.status}
                        size="small"
                        color={getStatusColor(file.status)}
                        variant="filled"
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(file.createdAt)}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <RowActionsMenu
                        file={file}
                        onView={(f) => navigate(`/home/${f._id}/voice-files`)}
                        onDownload={handleDownloadFile} // <-- Passed to Child
                        onDelete={(f) =>
                          setDeleteModal({
                            open: true,
                            file: f,
                            loading: false,
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModal.open}
        onClose={
          deleteModal.loading
            ? undefined
            : () => setDeleteModal({ open: false, file: null, loading: false })
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Voice File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. The file will be permanently deleted.
          </DialogContentText>
          {deleteModal.file && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  <strong>File:</strong> {deleteModal.file.fileName}
                </Typography>
                <Typography variant="body2">
                  <strong>Category:</strong> {deleteModal.file.promptCategory}
                </Typography>
                {deleteModal.file.durationReadable && (
                  <Typography variant="body2">
                    <strong>Duration:</strong>{" "}
                    {deleteModal.file.durationReadable}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() =>
              setDeleteModal({ open: false, file: null, loading: false })
            }
            disabled={deleteModal.loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteModal.loading}
            variant="contained"
            color="error"
            startIcon={
              deleteModal.loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteModal.loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoiceFilesList;
